/**
 * Maps a failed buyer creation to the copy shown to the person doing the
 * creating.
 *
 * The duplicate case is the only failure the backend describes precisely enough
 * to name. `buyerService` looks the Google place up before creating anything
 * (`services/buyer.ts` `getDuplicatedBuyerIfExists`, google-sourced addresses
 * only) and answers 409 with this message, verbatim:
 *
 *   "Duplicate: A Buyer for this Google Place already exists."
 *
 * The message is all there is to match on. buyerCreate is served over an HTTP
 * data source now, and its response template forwards only two values
 * (`$utils.error($utils.parseJson($ctx.result.body).message,
 * "$ctx.result.statusCode")`), so `errorType` on the wire is the string "409"
 * and the service's own `DuplicateGooglePlaceId` type — plus the conflicting
 * buyer it attaches — are dropped before they leave the cluster. Nothing to
 * branch on but the sentence, and nothing to link to yet.
 *
 * Everything else arrives as text written for engineers and is deliberately not
 * shown. The raw text still reaches the console through the Apollo error link.
 *
 * The remaining failures split in two, and the split matters because the two
 * ask for opposite things.
 *
 * **Rejected before anything was written.** The validator's bare `Bad Request`
 * and the authorizer's `Forbidden` both stop the call before the service runs,
 * as does the duplicate check above. Nothing exists, so the form can be
 * corrected and sent again.
 *
 * **Unknown.** A dropped connection, a timeout, or the service's blanket 500
 * says nothing about whether the buyer was written. `createBuyer` persists and
 * then returns, so a response lost on the way back leaves a buyer that exists
 * and a screen that says it does not. Telling someone nothing was saved here
 * sends them to create a second one. The copy says what is not known and sends
 * them to look first.
 */

export interface CreateBuyerFailureCopy {
  title: string;
  message: string;
}

/**
 * Matched as a substring: Apollo and AppSync both wrap the service message
 * (`GraphQL error: `, `Duplicate: `), so an equality check would miss it. This
 * is the same string the legacy admin matches on.
 */
const DUPLICATE_GOOGLE_PLACE_TEXT =
  'A Buyer for this Google Place already exists';

export const DUPLICATE_BUYER_FAILURE_COPY: CreateBuyerFailureCopy = {
  title: 'Buyer already exists',
  message:
    'This business is already a buyer in Choco. Search for it on the Buyers page instead of creating a second one.',
};

export const REJECTED_CREATE_BUYER_FAILURE_COPY: CreateBuyerFailureCopy = {
  title: 'Buyer not created',
  message: 'Nothing was saved. Check the details and try again.',
};

export const UNCONFIRMED_CREATE_BUYER_FAILURE_COPY: CreateBuyerFailureCopy = {
  title: 'Buyer may not have been created',
  message:
    'Search for this business on the Buyers page. Create it again only if it is not there.',
};

/**
 * The messages that prove the call was turned away before the service ran: the
 * validator's, and the authorizer's in both the forms it arrives in (AppSync
 * writes "Not Authorized to access <field> on type Mutation"). Matched as
 * substrings, like the duplicate text above.
 */
const REJECTED_BEFORE_WRITE_TEXTS = [
  'Bad Request',
  'Forbidden',
  'Not Authorized',
];

export const getCreateBuyerFailureCopy = (
  errorMessage: string
): CreateBuyerFailureCopy => {
  if (errorMessage.includes(DUPLICATE_GOOGLE_PLACE_TEXT)) {
    return DUPLICATE_BUYER_FAILURE_COPY;
  }

  if (REJECTED_BEFORE_WRITE_TEXTS.some((text) => errorMessage.includes(text))) {
    return REJECTED_CREATE_BUYER_FAILURE_COPY;
  }

  return UNCONFIRMED_CREATE_BUYER_FAILURE_COPY;
};
